// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset } from '../types';
import { num, rate, pct, times, delta, esc, COL } from '../format';
import { gloss } from '../glossary';
import { tipAttr, niceMax, linePoints } from '../charts';
import { navigate } from '../main';

export function renderOverview(root: HTMLElement, data: Dataset): void {
  const { national, meta } = data;
  const c = meta.counts;
  const years = national.years;
  const n = years.length;

  root.innerHTML = `
    <div class="hero">
      <div class="kicker">Prisoners in Australia · 30 June ${meta.latestYear}</div>
      <h2><span class="big">${num(c.total)}</span> people were in prison,<br/>a rate of <span class="big">${rate(c.rate)}</span> per 100,000 adults.</h2>
      <p>The number is up ${delta(c.growthSince2016Pct)} since ${meta.firstYear}. But a single rate hides two things this data makes plain — who is inside, and whether they have been sentenced.</p>
      <p>${gloss('atsi', 'Aboriginal and Torres Strait Islander')} people are imprisoned about <strong>${times(c.ratioStd)}</strong> as often as non-Indigenous people (${gloss('ageStandardised', 'age-standardised')}), a gap that has <em>widened</em> from ${times(c.ratioStd2016)}. And <strong>${pct(c.remandShare)}</strong> of prisoners are on ${gloss('remand', 'remand')} — unsentenced, presumed innocent — up from ${pct(c.remandShare2016)} in ${meta.firstYear}.</p>
    </div>

    <div class="stat-row">
      <div class="stat"><div class="stat-label">Prisoners</div><div class="stat-value">${num(c.total)}</div><div class="stat-note">${delta(c.growthSince2016Pct)} since ${meta.firstYear}</div></div>
      <div class="stat"><div class="stat-label">Imprisonment rate</div><div class="stat-value">${rate(c.rate)}</div><div class="stat-note">per 100,000 adults</div></div>
      <div class="stat hl"><div class="stat-label">Over-representation</div><div class="stat-value">${times(c.ratioStd)}</div><div class="stat-note">Aboriginal &amp; T.S.I. vs non-Indigenous (age-std)</div></div>
      <div class="stat"><div class="stat-label">On remand</div><div class="stat-value">${pct(c.remandShare)}</div><div class="stat-note">unsentenced, up from ${pct(c.remandShare2016)}</div></div>
      <div class="stat"><div class="stat-label">Aboriginal &amp; T.S.I. share</div><div class="stat-value">${pct(c.indigShare)}</div><div class="stat-note">${num(c.indigenousN)} prisoners</div></div>
      <div class="stat"><div class="stat-label">Prior imprisonment</div><div class="stat-value">${pct(c.priorShare)}</div><div class="stat-note">have been inside before</div></div>
    </div>

    <div class="note-band">This site measures <strong>imprisonment</strong> — an outcome of the justice system — <strong>not offending or crime</strong>. No place or group is presented as a scoreboard. If this affects you, Lifeline is 13&nbsp;11&nbsp;14 and 13YARN (Aboriginal &amp; Torres Strait Islander) is 13&nbsp;92&nbsp;76.</div>

    <div class="panel">
      <div class="panel-head"><h3>The prison population, ${years[0]}–${years[n - 1]}</h3><p>Sentenced and ${gloss('remand', 'unsentenced (remand)')} prisoners on 30 June each year. The grey remand band is doing most of the growing.</p></div>
      <div class="chart-wrap" id="ov-area"></div>
      <div class="legend">
        <span class="legend-item"><span class="legend-swatch" style="background:${COL.sentenced}"></span>Sentenced</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${COL.remand}"></span>Remand (unsentenced)</span>
      </div>
    </div>

    <div class="two-col" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-head"><h3>The widening gap</h3><p>Age-standardised imprisonment rate per 100,000, nationally.</p></div>
        <div class="chart-wrap" id="ov-gap"></div>
        <p class="mini-note">Aboriginal &amp; Torres Strait Islander ${rate(c.atsiRateStd, 0)} vs non-Indigenous ${rate(c.nonIndigRateStd, 0)} — a ${times(c.ratioStd)} gap, up from ${times(c.ratioStd2016)} in ${meta.firstYear}. <button class="linklike" data-go="gap">See every state →</button></p>
      </div>
      <div class="panel">
        <div class="panel-head"><h3>Remand keeps climbing</h3><p>Share of prisoners who are unsentenced.</p></div>
        <div class="chart-wrap" id="ov-remand"></div>
        <p class="mini-note">${pct(c.remandShare2016)} in ${meta.firstYear} → ${pct(c.remandShare)} in ${meta.latestYear}. <button class="linklike" data-go="remand">Why it matters →</button></p>
      </div>
    </div>
  `;

  drawArea(root.querySelector('#ov-area') as HTMLElement, data);
  drawGapLine(root.querySelector('#ov-gap') as HTMLElement, data);
  drawRemandLine(root.querySelector('#ov-remand') as HTMLElement, data);

  root.querySelectorAll<HTMLButtonElement>('[data-go]').forEach((b) => {
    b.style.cssText = 'background:none;border:0;padding:0;color:var(--accent-primary);font:inherit;font-size:var(--font-size-xs);cursor:pointer;text-decoration:underline';
    b.addEventListener('click', () => navigate({ view: b.dataset.go as any, state: undefined }));
  });
}

function drawArea(el: HTMLElement, data: Dataset): void {
  const { national } = data;
  const years = national.years;
  const W = 900, H = 320, m = { t: 14, r: 16, b: 28, l: 52 };
  const iw = W - m.l - m.r, ih = H - m.t - m.b;
  const maxV = niceMax(Math.max(...national.total));
  const x = (i: number) => m.l + (i / (years.length - 1)) * iw;
  const y = (v: number) => m.t + ih - (v / maxV) * ih;

  // stacked: sentenced bottom, remand on top → cumulative = total-ish
  const bottom = national.sentenced.map((v) => v ?? 0);
  const top = national.sentenced.map((v, i) => (v ?? 0) + (national.unsentenced[i] ?? 0));
  const areaPath = (upper: number[], lower: number[]) => {
    const up = upper.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    const dn = lower.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).reverse();
    return `M${up.join(' L')} L${dn.join(' L')} Z`;
  };

  const ticks = 5;
  const gridY = Array.from({ length: ticks + 1 }, (_, i) => (maxV / ticks) * i);

  let dots = '';
  for (let i = 0; i < years.length; i++) {
    dots += `<circle class="dot" cx="${x(i).toFixed(1)}" cy="${y(top[i]).toFixed(1)}" r="8" fill="transparent" stroke="none"
      ${tipAttr(`${years[i]}\n${num(national.total[i])} prisoners\nSentenced ${num(national.sentenced[i])} · Remand ${num(national.unsentenced[i])}`)} aria-label="${years[i]}: ${num(national.total[i])} prisoners"></circle>`;
  }

  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Prison population over time, sentenced and remand">
    ${gridY.map((g) => `<line class="grid-line" x1="${m.l}" y1="${y(g).toFixed(1)}" x2="${W - m.r}" y2="${y(g).toFixed(1)}"/><text class="axis-text" x="${m.l - 8}" y="${y(g) + 3}" text-anchor="end">${num(g)}</text>`).join('')}
    <path d="${areaPath(bottom, new Array(years.length).fill(0))}" fill="${COL.sentenced}" opacity="0.9"/>
    <path d="${areaPath(top, bottom)}" fill="${COL.remand}" opacity="0.9"/>
    ${years.map((yr, i) => `<text class="axis-text" x="${x(i).toFixed(1)}" y="${H - 8}" text-anchor="middle">${String(yr).slice(2)}</text>`).join('')}
    ${dots}
  </svg>`;
}

function drawGapLine(el: HTMLElement, data: Dataset): void {
  const r = data.rates;
  const idx = r.states.AUS;
  drawTwoLine(el, r.years, idx.stdIndig, idx.stdNon, COL.atsi, COL.nonIndig, ['Aboriginal & T.S.I.', 'Non-Indigenous'], (v) => num(v));
}

function drawRemandLine(el: HTMLElement, data: Dataset): void {
  const { national } = data;
  const share = national.total.map((t, i) => (t ? ((national.unsentenced[i] ?? 0) / t) * 100 : null));
  drawTwoLine(el, national.years, share, null, COL.remand, '', ['Remand share'], (v) => `${v.toFixed(1)}%`);
}

/** Shared small two-line (or one-line) chart. */
function drawTwoLine(
  el: HTMLElement, years: number[], a: (number | null)[], b: (number | null)[] | null,
  colA: string, colB: string, labels: string[], fmt: (v: number) => string,
): void {
  const W = 440, H = 240, m = { t: 12, r: 14, b: 26, l: 44 };
  const iw = W - m.l - m.r, ih = H - m.t - m.b;
  const all = [...a, ...(b ?? [])].filter((v): v is number => v != null);
  const maxV = niceMax(Math.max(...all));
  const x = (i: number) => m.l + (i / (years.length - 1)) * iw;
  const y = (v: number) => m.t + ih - (v / maxV) * ih;
  const ticks = 4;
  const gridY = Array.from({ length: ticks + 1 }, (_, i) => (maxV / ticks) * i);
  const line = (vals: (number | null)[], col: string) =>
    `<polyline class="series-line" points="${linePoints(vals, x, y)}" stroke="${col}"/>` +
    vals.map((v, i) => (v == null ? '' : `<circle class="dot" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="6" fill="transparent" ${tipAttr(`${years[i]}: ${fmt(v)}`)} aria-label="${years[i]}: ${fmt(v)}"></circle><circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="2.4" fill="${col}"/>`)).join('');
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(labels.join(' and '))} over time">
    ${gridY.map((g) => `<line class="grid-line" x1="${m.l}" y1="${y(g).toFixed(1)}" x2="${W - m.r}" y2="${y(g).toFixed(1)}"/><text class="axis-text" x="${m.l - 8}" y="${y(g) + 3}" text-anchor="end">${fmt(g)}</text>`).join('')}
    ${years.map((yr, i) => (i % 2 === 0 || i === years.length - 1 ? `<text class="axis-text" x="${x(i).toFixed(1)}" y="${H - 8}" text-anchor="middle">${String(yr).slice(2)}</text>` : '')).join('')}
    ${b ? line(b, colB) : ''}
    ${line(a, colA)}
  </svg>`;
}
